import React, { useMemo, useState } from 'react';

const getRowKey = (row, rowKey, index) => {
  if (!rowKey) return row?.id ?? index;
  if (typeof rowKey === 'function') return rowKey(row, index);
  return row?.[rowKey] ?? index;
};

const getAccessorValue = (row, accessor) => {
  if (typeof accessor === 'function') return accessor(row);
  if (typeof accessor === 'string') return row?.[accessor];
  return undefined;
};

const compareValues = (a, b) => {
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;

  const aNum = typeof a === 'string' ? Number(a) : NaN;
  const bNum = typeof b === 'string' ? Number(b) : NaN;
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;

  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
};

/**
 * Table genérica con ordenamiento por columna.
 *
 * Props:
 * - data: array
 * - columns: [{ id, header, accessor?, cell?, sortable?, sortAccessor?, sortComparator? }]
 * - rowKey: string | (row, index) => key
 * - className: string (default "table")
 * - emptyMessage: string
 * - initialSort: { id: string, direction?: "asc" | "desc" }
 */
const Table = ({
  data = [],
  columns = [],
  rowKey,
  className = 'table',
  emptyMessage = 'No hay datos para mostrar.',
  initialSort
}) => {
  const [sort, setSort] = useState(
    initialSort?.id
      ? { id: initialSort.id, direction: initialSort.direction === 'desc' ? 'desc' : 'asc' }
      : null
  );

  const sortedData = useMemo(() => {
    if (!sort?.id) return data || [];
    const col = columns.find((c) => c.id === sort.id);
    if (!col || col.sortable === false) return data || [];

    const dir = sort.direction === 'desc' ? -1 : 1;
    const decorated = (data || []).map((row, index) => ({ row, index }));
    decorated.sort((a, b) => {
      let res = 0;
      if (typeof col.sortComparator === 'function') {
        res = col.sortComparator(a.row, b.row);
      } else {
        const va = getAccessorValue(a.row, col.sortAccessor ?? col.accessor);
        const vb = getAccessorValue(b.row, col.sortAccessor ?? col.accessor);
        res = compareValues(va, vb);
      }
      if (res === 0) return a.index - b.index; // stable
      return res * dir;
    });
    return decorated.map((d) => d.row);
  }, [data, columns, sort]);

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (!prev || prev.id !== col.id) return { id: col.id, direction: 'asc' };
      if (prev.direction === 'asc') return { id: col.id, direction: 'desc' };
      return null;
    });
  };

  const sortIndicator = (colId) => {
    if (!sort || sort.id !== colId) return '';
    return sort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <table className={className}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.id}
              className={col.headerClassName}
              onClick={col.sortable === false ? undefined : () => toggleSort(col)}
              style={col.sortable === false ? { cursor: 'default' } : undefined}
            >
              {col.header}
              {col.sortable === false ? null : sortIndicator(col.id)}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {sortedData.map((row, index) => (
          <tr key={getRowKey(row, rowKey, index)}>
            {columns.map((col) => {
              const content =
                typeof col.cell === 'function' ? col.cell(row) : getAccessorValue(row, col.accessor);
              return <td key={col.id}>{content}</td>;
            })}
          </tr>
        ))}

        {sortedData.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="empty-state">
              {emptyMessage}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default Table;

